import { ChangeDetectorRef, Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from '../../dialog/dialog.service';
import { SettingService } from '../../setting/setting.service';
import { SendEmailModalComponent } from '../../shared/_components/send-email-modal/send-email-modal.component';
import { ViewEmailModalComponent } from '../../shared/_components/view-email-modal/view-email-modal.component';
import { dateTimeAsString, lastCopySavedAt } from '../../shared/date.helper';
import { GoogleService } from '../../shared/google.service';
import { Inspection, ProductType, SentEmail, UnderwritingStatus } from '../../shared/interfaces';
import { permissions } from '../../shared/permissions';
import { UserService } from '../../user/user.service';
import { CreateVersionModalComponent } from '../create-version-modal/create-version-modal.component';
import { FlagManagerComponent } from '../flag-manager/flag-manager.component';
import { InspectionGalleryComponent } from '../inspection-gallery/inspection-gallery.component';
import { InspectionLocationsComponent } from '../inspection-locations/inspection-locations.component';
import { InspectionReviewComponent } from '../inspection-review/inspection-review.component';
import { InspectionSnapshotComponent } from '../inspection-snapshot/inspection-snapshot.component';
import { InspectionService } from '../inspection.service';
import { InspectionStatus } from '../inspection.status';
import { getCurrentMandatoryReviewFilter, setCurrentMandatoryReviewFilter } from '../review.mandatory.filter.helper';
import {
  InspectionFilters,
  ReviewNavigationAction,
  ReviewNavigationComponent,
} from '../review.navigation/review.navigation.component';

@Component({
  selector: 'app-inspection-full-review',
  templateUrl: './inspection-full-review.component.html',
  styleUrls: ['./inspection-full-review.component.scss'],
})
export class InspectionFullReviewComponent implements OnInit {
  InspectionStatus = InspectionStatus;

  constructor(
    private inspectionService: InspectionService,
    private route: ActivatedRoute,
    private router: Router,
    public settingService: SettingService,
    public googleService: GoogleService,
    private dialog: DialogService,
    private toastr: ToastrService,
    public userService: UserService,
    private modalService: BsModalService,
    private cdr: ChangeDetectorRef,
  ) {}

  get showReportConfig() {
    return this._showReportConfig;
  }

  set showReportConfig(show) {
    this._showReportConfig = show;
    if (show) {
      this.userService.profile().then(currentUser => {
        const currentUserId = currentUser?.id;
        this.inspectionService.listReports(this.inspection.id).then(list => {
          const running = list.find(
            r => !r.completed && r.requestedByUserId != null && r.requestedByUserId === currentUserId,
          );

          if (running) {
            this.showReportConfig = false;
            this.dialog.alert().show({
              header: `Report generation is in progress`,
              message: `A report for this inspection is already being generated. You’ll receive a download link via email once it’s ready.`,
            });
          } else {
            // show only 3
            this.existingReports = list
              .filter(r => r.completed)
              .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
              .slice(0, 3);

            if (list.length === 0) {
              // There's no old reports to show
              this.oldReportsDismissed = true;
            }
          }
        });
      });
    } else {
      this.oldReportsDismissed = false;
    }
  }

  get showLetterConfig() {
    return this._showLetterConfig;
  }

  set showLetterConfig(show) {
    this._showLetterConfig = show;
    this.userService.hasPermissions([permissions.REC_LETTER_EMAIL.SEND]).then(res => {
      this.canSend = res;
    });
    if (show) {
      this.inspectionService.listLetters(this.inspection.id).then(list => {
        this.existingLetters = list
          .filter(letter => !letter.preview)
          .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
          .map(letter => ({
            ...letter,
            customerUrl: location.origin + '/recommendation-letter/expanded/' + encodeURIComponent(letter.hash),
          }));
      });
    } else {
      this.oldReportsDismissed = false;
    }
  }

  get reviewComponent() {
    return this._reviewComponent.first;
  }

  get galleryComponent() {
    return this._galleryComponent.first;
  }

  get snapshotComponent() {
    return this._snapshotComponent.first;
  }

  get locationsComponent() {
    return this._locationsComponent.first;
  }

  private _currentView;
  get currentView() {
    return this._currentView;
  }

  set currentView(value: string) {
    if (this._currentView === 'main' && value !== 'main') {
      this.reviewComponent.closePopup();
    }
    if (this._currentView === 'gallery' && value !== 'gallery') {
      this.galleryComponent.closePopup();
    }
    this._currentView = value;

    if (this._currentView === 'flagManager') {
      this._flagManagerComponent.first?.load();
      if (this.inspection) {
        this.refreshAnswers();
      }
    }
  }

  get noteText() {
    const sorting = this.filters.sorting !== 'followConfigurationOrder';
    const filters = this.navigationComponent.nActiveFilters - (sorting ? 1 : 0); // all filters except sorting

    switch (this.currentView) {
      case 'main':
        if (sorting) {
          return `Note: sorting is not applicable to this view`;
        }
        break;

      case 'snapshot':
        if (sorting && filters) {
          return `Note: sorting and some filters are not applicable to this view`;
        } else if (!sorting && filters) {
          return `Note: some filters are not applicable to this view`;
        } else if (sorting && !filters) {
          return `Note: sorting is not applicable to this view`;
        }
    }
  }

  get canCreateLetter() {
    const manager = this._flagManagerComponent.first;
    if (!manager) return false;
    if (!manager.allFlags) return false;
    if (!manager.groups) return false;

    const isCompleted = [null /* ungroupped */, ...manager.groups].every(g => manager.isCompleted(g));
    return isCompleted;
  }

  _showReportConfig = false;
  isRecLetterSend = { value: false };
  _showLetterConfig = false;
  isPreviewLetter = false;
  canSend;
  letterId;
  existingLetters;
  existingReports;
  createVersionFlag: boolean = false;
  productType: ProductType;
  reportParams = {
    showQuestions: true,
    showNonMandatory: getCurrentMandatoryReviewFilter() === 'all',
    showAdminComments: true,
    showInspectorComments: true,
    showFlags: true,
    showLegacyIds: true,
    showMarkupOverPictures: false,
    applyFilters: true,
  };

  oldReportsDismissed = false;

  scoresEnabled: boolean;
  inspection: Inspection;
  answers: any[];
  filters: InspectionFilters;

  dateTimeAsString = dateTimeAsString;
  lastCopySavedAt = lastCopySavedAt;

  @ViewChildren('review') _reviewComponent: QueryList<InspectionReviewComponent>;
  @ViewChildren('gallery') _galleryComponent: QueryList<InspectionGalleryComponent>;
  @ViewChildren('snapshot') _snapshotComponent: QueryList<InspectionSnapshotComponent>;
  @ViewChildren('locations') _locationsComponent: QueryList<InspectionLocationsComponent>;
  @ViewChildren('flagManager') _flagManagerComponent: QueryList<FlagManagerComponent>;
  @ViewChild('navigation', { static: true }) navigationComponent: ReviewNavigationComponent;
  @ViewChild('sendEmailModal', { static: true }) public sendEmailModal: SendEmailModalComponent;
  @ViewChild('createVersionModal', { static: true }) public createVersionModal: CreateVersionModalComponent;

  customerUrl: string;

  createReport() {
    this.dialog.confirm().show({
      header: 'Create inspection report',
      message: `The report will be generated and you'll receive a download link in your e-mail when it's ready`,
      confirmed: async () => {
        this.toastr.success('Your report is being generated');
        this.showReportConfig = false;
        await this.inspectionService.generateReport(this.inspection.id, {
          ...this.reportParams,
          filters: this.reportParams.applyFilters ? this.filters : undefined,
        });
      },
    });
  }

  downloadReport(report) {
    this.inspectionService.downloadReport(report);
  }

  async createLetter(selectedUser) {
    this.toastr.success('Your letter is being generated');
    const newLetter = await this.inspectionService.generateLetter(this.inspection.id, selectedUser);
    if (!this.inspection.hash) {
      this.inspection = await this.inspectionService.get(this.inspection.id);
      this.setCustomerUrl();
    }
    this.existingLetters.unshift(newLetter);
    await this.initExistingLetters(this.inspection.id);
    this.createVersionFlag = false;
  }

  async createNoIssuesLetter(selectedUser) {
    this.toastr.success('Your letter is being generated');
    const newLetter = await this.inspectionService.generateNoIssuesLetter(this.inspection.id, selectedUser);
    newLetter.data = Object.assign(newLetter.data || {}, { template: 'no-issues' });
    if (!this.inspection.hash) {
      this.inspection = await this.inspectionService.get(this.inspection.id);
      this.setCustomerUrl();
    }
    this.existingLetters.unshift(newLetter);
    this.createVersionFlag = false;
  }

  setCustomerUrl() {
    this.customerUrl = location.origin + '/recommendation-letter/' + encodeURIComponent(this.inspection.hash);
  }

  openLetter(letter) {
    window.open(`${this.customerUrl}?version=${letter.id}`, 'Recommendation Letter');
  }

  openLatestLetter() {
    window.open(`${this.customerUrl}?skipRegisteringView=true`, 'Recommendation Letter');
  }

  copy(url) {
    navigator.clipboard.writeText(url);
    this.toastr.success('Customer URL copied to clipboard!');
  }

  async ngOnInit() {
    this.scoresEnabled = await this.settingService.scoresEnabled();
    setCurrentMandatoryReviewFilter(this.scoresEnabled ? 'mandatoryOrScored' : 'mandatory');

    this.route.params.subscribe(async params => {
      this.currentView = params.view;

      if (!this.inspection) {
        this.inspection = await this.inspectionService.get(params.id);
        this.answers = await this.inspectionService.answers(params.id);
        // Validations problems must be fixed before entering this screen
        if (this.inspection.status === InspectionStatus.INVALID_SYNC) {
          this.router.navigateByUrl(`/inspection/review-invalid/${this.inspection.id}`);
          return;
        }

        this.cdr.detectChanges();
        // Inspection must be completed before entering this screen
        if (!this.inspection.completed) {
          this.router.navigateByUrl(`/inspection/form/${this.inspection.id}`);
          return;
        }
        this.navigationComponent.shortcut =
          this.currentView === 'flagManager' ? 'all-flagged' : 'all-mandatory-or-scored';
        this.navigationComponent.applyFilters();
        this.route.queryParams.subscribe(params => {
          if (params.clearFilters === 'true') {
            this.navigationComponent.clearFilters();
          }
        });
      }
      this.setCustomerUrl();
      await this.initExistingLetters(this.inspection.id);
    });
  }

  onNavAction(action: ReviewNavigationAction) {
    switch (action) {
      case ReviewNavigationAction.DUAL_MONITOR:
        if (this.currentView === 'main') {
          this.reviewComponent.openPopup();
        }
        if (this.currentView === 'gallery') {
          this.galleryComponent.togglePopup();
        }

        break;

      case ReviewNavigationAction.CREATE_OUTPUT:
        this.showReportConfig = true;
        break;

      case ReviewNavigationAction.CREATE_LETTER:
        this.showLetterConfig = true;
        break;
    }
  }

  initExistingLetters(InspectionId) {
    this.inspectionService.listLetters(InspectionId).then(list => {
      this.inspectionService.listLetters(InspectionId).then(list => {
        this.existingLetters = list
          .filter(letter => !letter.preview)
          .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
          .map(letter => ({
            ...letter,
            customerUrl: location.origin + '/recommendation-letter/expanded/' + encodeURIComponent(letter.hash),
          }));
      });
    });
  }

  onFilterChange(filters: InspectionFilters) {
    this.filters = filters;
  }

  openFilters() {
    this.navigationComponent.open = true;
    this.navigationComponent.pannel = 'filters';
  }

  async refreshAnswers() {
    this.answers = await this.inspectionService.answers(this.inspection.id);
    this.cdr.detectChanges();
  }

  async updateSpecificAnswer(answerId: number, updatedAnswer: any) {
    const answerIndex = this.answers.findIndex(a => a.id === answerId);
    if (answerIndex !== -1) {
      this.answers[answerIndex] = {
        ...this.answers[answerIndex],
        ...updatedAnswer,

        pictures: updatedAnswer.pictures || this.answers[answerIndex].pictures,
        flags: updatedAnswer.flags || this.answers[answerIndex].flags,
        comments: updatedAnswer.comments || this.answers[answerIndex].comments,
        changes: updatedAnswer.changes || this.answers[answerIndex].changes,
      };

      this.cdr.markForCheck();
    }
  }

  async updateMultipleAnswers(updates: { answerId: number; answer: any }[]) {
    updates.forEach(({ answerId, answer }) => {
      this.updateSpecificAnswer(answerId, answer);
    });
  }

  onPopupClosed() {
    this.navigationComponent.isPopupOpen = false;
  }

  onPopupOpened() {
    this.navigationComponent.isPopupOpen = true;
  }

  openSendEmail(letterId) {
    this.letterId = letterId;
    this.sendEmailModal.show();
  }

  createVersion() {
    if (this.canCreateLetter) {
      this.isPreviewLetter = false;
      this.createVersionFlag = true;
      this.createVersionModal.show();
    }
  }

  previewVersion() {
    if (this.canCreateLetter) {
      this.isPreviewLetter = true;
      this.createVersionFlag = true;
      this.createVersionModal.show();
    }
  }

  createNoIssueVersion() {
    if (this.canCreateLetter) {
      this.isPreviewLetter = false;
      this.createVersionFlag = false;
      this.createVersionModal.show();
    }
  }

  create(selectedUser) {
    if (this.canCreateLetter && this.createVersionFlag) {
      this.createLetter(selectedUser);
    }

    if (!this.createVersionFlag) {
      this.createNoIssuesLetter(selectedUser);
    }
  }

  async previewLetter(selectedUser) {
    if (this.canCreateLetter && this.createVersionFlag) {
      this.toastr.success('Your Preview letter is being generated');
      const newLetter = await this.inspectionService.generateLetter(this.inspection.id, selectedUser, true);
      if (!this.inspection.hash) {
        this.inspection = await this.inspectionService.get(this.inspection.id);
        this.setCustomerUrl();
      }

      window.open(`${this.customerUrl}?version=${newLetter.id}`, 'Recommendation Letter');
    }
  }

  previewEmail(letterEmail: SentEmail) {
    this.modalService.show(ViewEmailModalComponent, {
      class: 'modal-lg',
      initialState: {
        letterEmail: letterEmail,
        productType: this.inspection.policy.productType,
      },
    });
  }

  async downloadEmail(letterEmail: SentEmail) {
    this.inspectionService.downloadSentEmail(letterEmail.id).then(data => {
      const blob = new Blob([data as any], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'RecLetterEmail.pdf';
      link.click();
      window.URL.revokeObjectURL(url);
    });
  }

  async handleEmailSent(sentEmail) {
    await this.initExistingLetters(this.inspection.id);
    if (
      this.inspection.underwritingStatus == UnderwritingStatus.PREPARE_FLAGS ||
      this.inspection.underwritingStatus == UnderwritingStatus.PREPARATION_IN_PROGRESS
    ) {
      await this.inspectionService.setUnderwritingStatus(this.inspection.id, UnderwritingStatus.LETTER_SENT);
      this.isRecLetterSend = { value: true };
    }
  }
}
